import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CataloguePriceResource,
  CataloguePricesController_at_v1,
  type UserResource,
} from '@repo/api-client';
import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle, IconClockSearch, IconHistoryOff } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { isNotFound, messageForFailure } from '@/api/failure';
import { isoInstantFrom, localInstantSchema } from '@/lib/local-instant';
import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { formatInstant } from './instant';

/**
 * What this item cost and sold for at a moment that has passed.
 *
 * ## Why it exists at all
 *
 * A receipt from March names a `priceId`, and Reporting computes margin from
 * these rows. "What did this cost on the day of that receipt" is the question
 * `GET /v1/catalogue/items/{id}/prices/at?instant=` answers directly, and it is
 * the reason the history is a record rather than an audit curiosity behind a
 * tab: without it, answering means scanning the ledger by eye and getting the
 * boundary wrong on the day a price moved.
 *
 * ## The refusal happens here, before the request
 *
 * `?instant=` is strict ISO 8601 and the API refuses `2026-02-30` rather than
 * reading it as 2 March. **`Date` does the opposite**, silently, which is the
 * whole reason `lib/local-instant.ts` exists — see the note there. This field
 * therefore refuses exactly what the server refuses, at the field, rather than
 * sending an instant that would either be rejected in a round trip or, worse,
 * quietly rewritten into a different day by a well-meaning parse on the way out.
 *
 * ## "None" is an answer, not an empty result
 *
 * An instant before the item had any price answers **404**, and that is the
 * honest answer rather than an empty row: there genuinely was no price then. The
 * screen says so in those words. A blank amount would read as a rendering fault
 * and — on a screen about money — as ₹0.00, which is a real and different price.
 *
 * The same 404 also covers an unknown item and one outside the caller's reach.
 * Reaching it that way is not possible from here: this panel only renders under
 * a header that has already read the item, so by the time the field can be typed
 * in, the item is known to exist and to be reachable. Worth stating, because the
 * conflation is in the contract and the wording below leans on it not applying.
 */

const asOfSchema = z.object({ at: localInstantSchema });

/**
 * Every state the answer can be in. One value, so two cannot coexist.
 *
 * `at` is the **ISO instant that was asked about**, not the local string that
 * was typed: it is rendered back through the same formatter the ledger rows use,
 * so "no price was in force at…" names the moment in exactly the words the row
 * above it would have. Keeping the raw field value here instead would put a
 * `2026-03-03T14:00` in a sentence beside a `3 Mar 2026, 14:00`.
 */
type Answer =
  | { readonly kind: 'idle' }
  | { readonly kind: 'looking' }
  | { readonly kind: 'found'; readonly price: CataloguePriceResource }
  | { readonly kind: 'none'; readonly at: string }
  | { readonly kind: 'failed'; readonly error: unknown };

export function PriceAsOf({
  itemId,
  actors,
}: {
  itemId: string;

  /** Resolved by the screen above, so both places name a person the same way. */
  actors: ReadonlyMap<string, UserResource>;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(asOfSchema),
    mode: 'onTouched',
    defaultValues: { at: '' },
  });

  const [answer, setAnswer] = useState<Answer>({ kind: 'idle' });

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: z.infer<typeof asOfSchema>) => {
    const instant = isoInstantFrom(values.at);

    // The resolver has already refused everything this can be null for. Read as
    // an impossible state rather than asserted away with a `!`: a change to the
    // schema that let a malformed value through would show as a field that
    // refuses rather than as a request carrying `undefined`.
    if (instant === null) {
      form.setError('at', { message: 'That is not a real date and time.' });

      return;
    }

    setAnswer({ kind: 'looking' });

    try {
      const price = await client.request(CataloguePricesController_at_v1, {
        path: { id: itemId },

        // The instant the server is asked about is UTC, converted once on the
        // way out of `isoInstantFrom`. What was typed stays in the field, in the
        // zone the rows are rendered in.
        query: { instant },
      });

      setAnswer({ kind: 'found', price });
    } catch (error: unknown) {
      // Branched on the code and never on the status, per docs/ui-conventions.md
      // — and this is the branch where the distinction earns itself. A 404 here
      // is an answer to the question ("no price then") rather than a failure to
      // answer it, and `messageForFailure` would word it as "that record no
      // longer exists", which is about a record that was deleted.
      setAnswer(isNotFound(error) ? { kind: 'none', at: instant } : { kind: 'failed', error });
    }
  };

  return (
    <section
      aria-label="Price at a past moment"
      // A band of the field, not a card on it. The panel is the surface now, so
      // the bordered, rounded enamel box this used to draw was a card inside a
      // card — and with the field uncapped the one input in it stretched the
      // width of the panel. What is left is what a band is: the head's own
      // gutter, a measure for the field, and a rule between this and the records
      // under it. The negative margin is `ScreenTable`'s, for the same reason —
      // a rule reads as a band's edge only if it runs the full width of the
      // field.
      className="-mx-4 border-b border-border px-4 pb-4 lg:-mx-7 lg:px-7"
    >
      <div className="max-w-xl">
        <Form {...form}>
          <form noValidate onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}>
            <FormField
              control={form.control}
              name="at"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required className={fieldLabel}>
                    Price in force at
                  </FormLabel>

                  {/* The button is **inside the field**, on the input's own row,
                      with the description under both. It used to be the form's
                      second flex child, bottom-aligned against a description
                      that was one line because the input was the width of the
                      card. `FormControl` still wraps the input, so the id, the
                      description and the message are wired by the component. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                    <FormControl
                      render={
                        <Input
                          // A native date-and-time field rather than a box
                          // asking a park manager to write ISO 8601. It is typed
                          // in the zone the rows are shown in, so a moment can be
                          // read off the ledger and typed straight back in
                          // without anybody converting to UTC in their head.
                          type="datetime-local"
                          className={`${fieldSurface} h-11 numeric sm:flex-1`}
                          required
                          {...field}
                        />
                      }
                    />

                    {/* `outline`, not the accent. The screen's one primary action
                        is Reprice; a second green button here would leave two
                        equal-looking controls where one writes to the ledger and
                        one only reads it. */}
                    <Button
                      type="submit"
                      variant="outline"
                      className="h-11 sm:shrink-0"
                      disabled={pending}
                    >
                      <IconClockSearch data-icon="inline-start" />
                      {pending ? 'Looking…' : 'Look up'}
                    </Button>
                  </div>

                  <FormDescription>
                    The moment to answer for — the date on a receipt, and roughly when it was rung
                    up.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>

        {/* `polite`: the answer arrives beside a field somebody has just used, and
          is announced without cutting across what they are doing.

          The region is **always in the DOM**, empty while there is nothing to
          say. A live region that mounts at the same moment as its content is not
          reliably announced — the browser has nothing to compare against — and
          the whole point of it here is that the first answer, which is the one
          somebody is waiting for, gets read out. */}
        <div
          aria-live="polite"
          className={answer.kind === 'idle' ? undefined : 'mt-5 border-t border-border pt-4'}
        >
          <AnswerBody answer={answer} actors={actors} />
        </div>
      </div>
    </section>
  );
}

function AnswerBody({
  answer,
  actors,
}: {
  answer: Answer;
  actors: ReadonlyMap<string, UserResource>;
}) {
  switch (answer.kind) {
    case 'idle':
      return null;

    case 'looking':
      return <p className="text-sm text-muted-foreground">Looking…</p>;

    case 'none': {
      return (
        <div className="flex items-start gap-2.5">
          <IconHistoryOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">No price was in force then</p>
            <p className="text-sm text-muted-foreground">
              This item had no price at{' '}
              <span className="font-mono text-foreground numeric">{formatInstant(answer.at)}</span>{' '}
              — the moment is before its first one was set, so nothing was sold at any price then.
            </p>
          </div>
        </div>
      );
    }

    case 'failed':
      return (
        <div className="flex items-start gap-2.5">
          <IconAlertTriangle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">That lookup did not answer</p>
            {/* The code becomes words in one place for the whole app. A screen
                that worded this itself would re-separate refusals the API merges
                on purpose. */}
            <p className="text-sm text-muted-foreground">{messageForFailure(answer.error)}</p>
          </div>
        </div>
      );

    case 'found': {
      const actor = actors.get(answer.price.setByActorId);

      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
            <Amount label="Selling" paise={answer.price.sellingPaise} emphasised />
            <Amount label="Cost" paise={answer.price.costPaise} />
          </div>

          <p className="text-sm text-muted-foreground">
            In force from{' '}
            <span className="font-mono text-foreground numeric">
              {formatInstant(answer.price.effectiveFrom)}
            </span>
            , set by{' '}
            {actor === undefined ? (
              <span className="font-mono numeric">{answer.price.setByActorId}</span>
            ) : (
              <span className="text-foreground">{actor.displayName}</span>
            )}
            . It stayed in force until the next entry above it, if there is one.
          </p>
        </div>
      );
    }
  }
}

/** One amount under its signage label, the way a standing fact is written. */
function Amount({
  label,
  paise,
  emphasised = false,
}: {
  label: string;
  paise: number;
  emphasised?: boolean;
}) {
  return (
    <div>
      <p className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase">
        {label}
      </p>
      <p
        className={`mt-1 font-mono numeric ${emphasised ? 'text-lg text-foreground' : 'text-lg text-muted-foreground'}`}
      >
        {formatPaise(paise)}
      </p>
    </div>
  );
}
